<template>
    <div ref="dataLoaderElement" class="stretch-layout">
        <div v-if="!props.hideSearch || hasFilterInputs || $slots.buttons || props.onScroll" class="flex w-full justify-between mb-5 sm:mb-20 items-center"
             :class="[props.breakHeader ? 'flex-column-reverse sm:flex-row' : 'flex-row', {'px-12 sm:px-16': props.fullWidth}]">
            <div class="flex-grow-x-1 max-w-800" :class="[{'w-full md:w-unset': props.breakHeader}, props.breakHeader ? 'sm:mr-8' : 'mr-8']">
                <wrapper :class="[props.breakHeader && $slots.buttons ? 'mt-8 sm:mt-0' : 'mt-0']" left stretch :break-column="props.showInputsOnMobile ? 'xs' : null" break-reverse>
                    <form-input
                        v-if="!props.hideSearch"
                        v-model="filterData.search"
                        :icon="icons.search"
                        :placeholder="$t('action.search')"
                        type="search"
                        enterkeyhint="search"
                        clearable
                        class="sm:max-w-400 sm:min-w-300"
                        inverted
                        hide-details/>

                    <client-only>
                        <slot v-if="showInputsOnTable" name="inputs" v-bind:data="filterData" v-bind:onTable="true"/>
                    </client-only>

                    <client-only>
                        <div v-if="hasFilterInputs" class="flex">
                            <tooltip :text="$t('action.show_extended_filter')" bottom>
                                <btn :color="filterExists ? (props.color ?? $config.public.colors.primary) : null" @click.stop="openFilterModal">
                                    <Icon :icon="icons.extended"/>
                                </btn>
                            </tooltip>
                            <tooltip v-if="filterExists" :text="$t('action.reset_filter')" bottom class="ml-8">
                                <btn @click.prevent="reset">
                                    <Icon :icon="icons.reset"/>
                                </btn>
                            </tooltip>
                        </div>
                    </client-only>
                </wrapper>
            </div>

            <client-only>
                <div v-if="$slots.buttons || props.onScroll" :class="{'w-full md:w-unset': breakHeader}">
                    <wrapper grow>
                        <slot name="buttons"/>
                        <tooltip v-if="props.onScroll && !hidePaginationControls" :text="$t('action.refresh')" top class="flex justify-end">
                            <btn @click.prevent="reload(1)" icon :loading="isLoading">
                                <Icon :icon="icons.refresh"/>
                            </btn>
                        </tooltip>
                    </wrapper>
                </div>
            </client-only>
        </div>

        <div v-if="$slots.header" class="mt-10 sm:mt-0" :class="{'px-16': props.fullWidth}">
            <slot name="header"/>
        </div>

        <div class="overflow-x-auto stretch-layout" :class="[tableMaxHeightStyle ? 'overflow-y-auto' : 'overflow-y-hidden', {'sm:px-16': props.fullWidth}]" :style="{maxHeight: tableMaxHeightStyle}">
            <table class="table"
                   :class="{'table-fixed-header': props.fixedHeader && $breakpoint.mdAndUp, 'table-dense': dense, 'table-full-width': props.fullWidth, 'table-mobile-expand': displayWithMobileExpand}">
                <thead v-if="loadedData.length || filterExists">
					<tr class="table-header">
						<table-header-row-data v-for="header in displayHeaders" :header="header" :key="header.value" :order="orderProps" :sortable="!props.disableSort" @sort="orderByHeader">
							<template v-if="$slots['header.' + header.value]">
								<slot :name="'header.' + header.value" v-bind:header="header"/>
							</template>
						</table-header-row-data>
					</tr>
                </thead>
                <tbody>

                <tr v-if="error">
                    <td :colspan="displayHeaders.length">
                        <lazy-alert :icon="icons.warning" type="warning" text large class="h-full my-16" :content="error.message">
							<template v-if="error.retryable" #button>
								<btn @click="reload(1)" color="warning lighten">
									<Icon :icon="icons.refresh"/>
									<span>{{ $t('action.try_again') }}</span>
								</btn>
							</template>
                        </lazy-alert>
                    </td>
                </tr>
                <template v-else-if="isLoading && (!props.onScroll || replaceData)">
                    <table-loading-row v-for="(item, itemKey) in skeletonRows" :key="'loader-' + itemKey" :columns="displayHeaders.length"/>
                </template>
                <tr v-else-if="!loadedData || !loadedData.length">
                    <td :colspan="displayHeaders.length">
                        <lazy-missing-info :text="missingText" :button="filterExists ? null : props.missingButton" :to="filterExists ? null : props.missingTo" class="h-full my-16"/>
                    </td>
                </tr>
                <template v-else>
                    <template v-for="(item, itemKey) in loadedData" :key="`item-group-${item.id ? `${item.id}-${itemKey}` : itemKey}`">
                        <table-group-row v-if="item._divider" :group="item" :columns="displayHeaders"/>
                        <template v-else>
                            <table-group-row v-if="props.groupProp && (itemKey === 0 || item[props.groupProp] !== loadedData[itemKey-1][props.groupProp]) && getItemGroup(item)"
                                             :group="getItemGroup(item)" :columns="displayHeaders"/>

							<slot name="row" v-bind:item="item" v-bind:index="itemKey" v-bind:headers="displayHeaders"
								  v-bind:reduced="displayWithMobileExpand"
								  v-bind:displayWithMobileExpand="displayWithMobileExpand"
								  v-bind:toggleRowExpand="toggleRowExpand">

								<tr :class="{'cursor-pointer hover:bg-gray-100': canBeExpanded(item), 'opacity-75': props.disabledProp && item[props.disabledProp]}" @click="toggleRowExpand(item)">
									<table-body-row-data v-for="header in displayHeaders" :header="header" :key="`item-${item.id ? `${item.id}-${itemKey}` : itemKey}`" :item="item" :reduced="displayWithMobileExpand" :expandable="canBeExpanded(item)" @expand="toggleRowExpand(item)">
										<template v-if="$slots['item.' + header.value]">
											<slot :name="'item.' + header.value" v-bind:item="item" v-bind:index="itemKey" v-bind:reduced="displayWithMobileExpand" v-bind:expanded="false"/>
										</template>

										<template #actions>
											<slot name="actions" v-bind:item="item"/>
										</template>
									</table-body-row-data>
								</tr>

							</slot>

                            <template v-if="item._expand">
                                <tr v-if="displayWithMobileExpand" class="bg-gray-100" :key="`item-${item.id ? `${item.id}-${itemKey}` : itemKey}-expand`">
                                    <td :colspan="displayHeaders.length" class="p-0">
                                        <div class="max-w-screen">
                                            <div class="p-8">
                                                <lazy-description-list small>
                                                    <lazy-description-list-item v-for="(expandHeader, expandHeaderIndex) in headers" :key="`expand-header-${expandHeaderIndex}`"
                                                                           :title="expandHeader.text">
                                                        <template #text>
                                                            <slot :name="'item.' + expandHeader.value" v-bind:item="item" v-bind:index="itemKey" v-bind:reduced="false" v-bind:expanded="true"/>
                                                            <table-body-cell v-if="!$slots['item.' + expandHeader.value]" :item="item" :column="expandHeader"/>
                                                        </template>
                                                    </lazy-description-list-item>
                                                </lazy-description-list>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <template v-if="item._expand_loading">
                                    <table-loading-row v-for="i in (props.subRowsCountProp && item[props.subRowsCountProp] ? item[props.subRowsCountProp] : 1)" :key="`loading-expand-${i}`"
                                                       :columns="displayHeaders.length"/>
                                </template>
                                <template v-else>

                                    <template v-for="(subItem, subItemKey) in item[props.subRowsProp]" :key="`item-${item.id ? `${item.id}-${itemKey}` : itemKey}-sub-${subItem.id ?? subItemKey}`">

										<tr class="sub-row" :class="{'cursor-pointer hover:bg-gray-100': canBeExpanded(subItem), 'opacity-75': props.disabledProp && subItem[props.disabledProp]}" @click="toggleSubRowExpand(subItem, item)">
											<table-body-row-data v-for="header in displayHeaders" :header="header" :key="`item-${item.id ? `${item.id}-${itemKey}` : itemKey}`" :item="subItem"
																 :parent="item" :reduced="displayWithMobileExpand" :expandable="canBeExpanded(subItem)" @expand="toggleSubRowExpand(subItem, item)">
												<template v-if="$slots['item.' + header.value]">
													<slot :name="'item.' + header.value" v-bind:parent="item" v-bind:item="subItem" v-bind:reduced="displayWithMobileExpand" v-bind:expanded="false"/>
												</template>

												<template #actions>
													<slot name="actions" v-bind:parent="item" v-bind:item="subItem"/>
												</template>
											</table-body-row-data>
										</tr>

                                        <template v-if="subItem._expand">
                                            <tr v-if="displayWithMobileExpand" class="bg-gray-100" :key="`item-${item.id ? `${item.id}-${itemKey}` : itemKey}-sub-${subItem.id ?? subItemKey}-expand`">
                                                <td :colspan="displayHeaders.length" class="p-0">
                                                    <div class="max-w-screen">
                                                        <div class="p-8">
                                                            <lazy-description-list small>
                                                                <lazy-description-list-item v-for="(expandHeader, expandHeaderIndex) in headers"
                                                                                       :key="`expand-header-${expandHeaderIndex}`" :title="expandHeader.text">
                                                                    <template #text>
                                                                        <slot :name="'item.' + expandHeader.value" v-bind:parent="item" v-bind:item="subItem"
                                                                              v-bind:index="subItemKey" v-bind:reduced="false" v-bind:expanded="true"/>
                                                                        <table-body-cell v-if="!$slots['item.' + expandHeader.value]" :item="subItem" :parent="item"
                                                                                         :column="expandHeader"/>
                                                                    </template>
                                                                </lazy-description-list-item>
                                                            </lazy-description-list>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                            <template v-if="subItem._expand_loading">
                                                <table-loading-row v-for="i in (props.subRowsCountProp && subItem[props.subRowsCountProp] ? subItem[props.subRowsCountProp] : 1)"
                                                                   :key="`loading-sub-expand-${i}`" :columns="displayHeaders.length"/>
                                            </template>
                                            <template v-else>
												<tr v-for="(subSubItem, subSubItemKey) in subItem[props.subRowsProp]" class="sub-row"
													:key="`item-${item.id ? `${item.id}-${itemKey}` : itemKey}-sub-${subItem.id ?? subItemKey}-${subSubItem.id ?? subSubItemKey}`"
													:class="{'opacity-75': props.disabledProp && subSubItem[props.disabledProp]}">
													<table-body-row-data v-for="header in displayHeaders" :header="header" :key="`item-${item.id ? `${item.id}-${itemKey}` : itemKey}`" :item="subSubItem"
																		 :sub-parent="subItem"
																		 :parent="item" :reduced="displayWithMobileExpand">
														<template v-if="$slots['item.' + header.value]">
															<slot :name="'item.' + header.value" v-bind:parent="item" v-bind:subParent="subItem" v-bind:item="subSubItem"
																  v-bind:reduced="displayWithMobileExpand" v-bind:expanded="false"/>
														</template>

														<template #actions>
															<slot name="actions" v-bind:parent="item" v-bind:subParent="subItem" v-bind:item="subSubItem"/>
														</template>
													</table-body-row-data>
												</tr>

                                            </template>
                                        </template>
                                    </template>
                                </template>
                            </template>

                            <slot v-if="props.groupProp && (itemKey === (loadedData.length - 1) || item[props.groupProp] !== loadedData[itemKey+1][props.groupProp]) && getItemGroup(item)" name="group_footer" v-bind:group="getItemGroup(item)"/>

                        </template>
                    </template>
                </template>
                </tbody>
            </table>
        </div>
        <div class="flex items-start justify-between flex-column lg:flex-row mt-20" :class="{'px-16': props.fullWidth}">
            <div>
                <slot name="footer"/>
            </div>

            <div v-if="props.onScroll" v-intersect="{handler: onIntersect, options: {rootMargin: `250px`}}"/>
            <div v-else-if="!hidePaginationControls" class="w-full lg:w-auto lg:ml-16">
                <wrapper break-column="xs" break-reverse>
                    <div class="flex items-center justify-end">
                        <div class="flex items-center">
                            <span class="text-xs mr-8 xl:mr-16 leading-normal whitespace-no-wrap">{{ $t('label.per_page') }}:</span>

                            <div class="w-64">
                                <form-select v-model="filterData.limit" :color="props.color" inverted small hide-details :options="[10, 25, 50, 100, 250]"/>
                            </div>
                        </div>

                        <span v-if="!isLoading" class="text-xs ml-8 xl:ml-16 leading-normal whitespace-no-wrap">
							{{ fromPosition }}-{{ toPosition }} {{ $t('conjunction.from') }} {{ availableResults }}</span>
                    </div>

                    <div class="flex items-center ml-8 xl:ml-16">
                        <div>
                            <tooltip :text="$t('action.refresh')" top>
                                <btn @click.prevent="reload()" icon :loading="isLoading">
                                    <Icon :icon="icons.refresh"/>
                                </btn>
                            </tooltip>
                        </div>
                        <wrapper class="ml-8 xl:ml-16 flex-grow-1" grow>
                            <btn icon @click="loadPrevious(true)" :disabled="page <= 1">
                                <Icon :icon="icons.prev"/>
                            </btn>
                            <btn icon @click="loadNext(true)" :disabled="!hasMore">
                                <Icon :icon="icons.next"/>
                            </btn>
                        </wrapper>
                    </div>
                </wrapper>
            </div>
        </div>

        <client-only>
			<lazy-modal v-if="hasFilterInputs" v-model="showFilterModal" :title="$t('label.extended_filter')">
				<slot name="inputs" v-bind:data="filterModalInputData" v-bind:onTable="false"/>

				<slot name="filter" v-bind:data="filterModalInputData"/>

				<template #button>
					<btn @click.prevent="applyFilterModalInputs" color="success">
						<Icon :icon="icons.submit"/>
						<span>{{ $t('action.apply_filter') }}</span>
					</btn>
				</template>
			</lazy-modal>
        </client-only>
    </div>
</template>

<script setup lang="ts">

import {DataLoaderComponentEmits, DataLoaderComponentPropDefaults, type DataLoaderComponentProps, useDataLoaderComponent} from "../../composables/useDataLoaderComponent";
import {type TableComponentProps, useTableComponent} from "../../composables/useTableComponent";
import {nextIcon, refreshIcon, searchIcon, submitIcon, warningIcon, resetIcon} from "../../config";
import {mdiChevronLeft, mdiTune} from "@mdi/js";
import type {Ref} from "vue";
import {onMounted, ref, watch, computed, nextTick} from "vue";
import {useNuxtApp, useI18n, useSlots, useRoute} from '#imports';

const props = withDefaults(defineProps<{

	// Whether to hide the search input to filter the results
	hideSearch?: boolean,

	// Whether the rows of the table shall receive a smaller height
	dense?: boolean,

	// Whether the header columns shall be aligned in columns on 'md' breakpoint
	breakHeader?: boolean,

	// Whether to hide the controls to paginate through the pages
	hidePaginationControls?: boolean,

	// Whether to show the custom input slot components on mobile devices
	showInputsOnMobile?: boolean,

} & DataLoaderComponentProps & TableComponentProps>(), {
	...DataLoaderComponentPropDefaults,
	persistFilterKeys: ['search', 'order', 'dir'],
	breakHeader: true,
	filter: {}
});

const emit = defineEmits([...DataLoaderComponentEmits, 'expand-row', 'expand-sub-row']);

const {t} = useI18n();
const {$breakpoint, isHydrating, $auth} = useNuxtApp();
const slots = useSlots();

const showFilterModal = ref(false);
const filterModalInputData = ref({});

const route = useRoute();

let initialFilter: {[key: string]: any} = {limit: 25};
props.persistFilterKeys.forEach(key => {
	if (route.query[key]) {
		// Ensure the orders are specified as arrays to simplify the access
		if (['order', 'dir'].includes(key)) {
			initialFilter[key] = Array.isArray(route.query[key]) ? route.query[key] : [route.query[key]];
		} else {
			initialFilter[key] = route.query[key];
		}
	}
});

const filterData: Ref<{[key: string]: any}> = ref({...initialFilter, ...props.filter});

const icons = {
	submit: submitIcon,
	refresh: refreshIcon,
	extended: mdiTune,
	prev: mdiChevronLeft,
	next: nextIcon,
	search: searchIcon,
	warning: warningIcon,
	reset: resetIcon,
}

// The HTML element on which the component is mounted
const dataLoaderElement: Ref<HTMLElement|null> = ref(null);

const {
	isLoading, loadedData, loadNext, loadPrevious, reload, load, reset, hasMore,
	availableResults,
	filterExists,
	onIntersect,
	replaceData, page,
	fromPosition,
	toPosition, error
} = useDataLoaderComponent(dataLoaderElement, props, emit, filterData);

const {displayHeaders, canBeExpanded, tableMaxHeightStyle, displayWithMobileExpand, getItemGroup} = useTableComponent(props);

const missingText = computed(() => {
	if (filterExists.value) {
		return props.noResultsText ? props.noResultsText : t('missing.search')
	}
	return props.noDataText ? props.noDataText : t('missing.data')
});

const skeletonRows = computed(() => {
	if (!props.onScroll && loadedData.value && loadedData.value.length) {
		return loadedData.value.length;
	}

	if (availableResults.value && availableResults.value > 0) {
		return filterData.value.limit > -1 ? Math.min(availableResults.value!, filterData.value.limit) : availableResults.value
	}

	return filterData.value.limit > -1 ? filterData.value.limit : 10;
});

// Whether the additional inputs shall be shown directly on the table and not on the extended filters
const showInputsOnTable = computed(() => {
	return props.showInputsOnMobile || $breakpoint.lgAndUp;
});

const hasFilterInputs = computed(() => {
	return !!(slots.filter || (!showInputsOnTable.value && slots.inputs));
});

// The columns to order the results by default as keys with the order direction as values
const orderProps = computed(() => {
	if (!filterData.value.order) {
		return {}
	}

	const orderProps = Array.isArray(filterData.value.order) ? filterData.value.order : [filterData.value.order];
	const orderDirs = Array.isArray(filterData.value.dir) ? filterData.value.dir : [filterData.value.dir];
	const parsedOrderProps = {};
	orderProps.forEach((orderProp, orderPropIndex) => {
		parsedOrderProps[orderProp] = orderDirs.length >= (orderPropIndex + 1) ? orderDirs[orderPropIndex] : 'asc';
	})

	return parsedOrderProps
});

// We solve this via a method and not a watcher since we do not want the extended filter to be immediately applied to the lazy loading filter -> would cause an immediate API request
function applyFilterModalInputs() {
	filterData.value = filterModalInputData.value;
	showFilterModal.value = false;
}

function openFilterModal() {
	// We clone the filter data to not immediately trigger a filter action when an input changes but when the user confirms the filter modal
	filterModalInputData.value = {...filterData.value};

	nextTick(function () {
		showFilterModal.value = true
	});
}

function toggleRowExpand(row) {
	if (!canBeExpanded(row)) {
		return;
	}
	row['_expand'] = !row._expand
	emit('expand-row', row, row._expand);
}

function toggleSubRowExpand(row, parentRow) {
	if (!canBeExpanded(row)) {
		return;
	}
	row['_expand'] = !row._expand
	emit('expand-sub-row', row, parentRow, row._expand);
}

function orderByHeader(header) {
	if (props.disableSort || header.sortable === false) {
		return;
	}

	const sortValue = header.sort_value ? header.sort_value : header.value;

	const existingSortIndex = filterData.value.order && filterData.value.order.length ? filterData.value.order.indexOf(sortValue) : -1;

	if (existingSortIndex > -1) {
		const existingSortDir = existingSortIndex > -1 && filterData.value.dir && filterData.value.dir.length > existingSortIndex ? filterData.value.dir[existingSortIndex] : null;
		if (existingSortDir === 'desc') {
			filterData.value.order.splice(existingSortIndex, 1)
			filterData.value.dir.splice(existingSortIndex, 1)
		} else {
			filterData.value.dir[existingSortIndex] = 'desc'
		}
	} else {
		if (!filterData.value.order) {
			filterData.value['order'] = []
		}
		if (!filterData.value.dir) {
			filterData.value['dir'] = []
		}

		filterData.value.order.push(sortValue)
		filterData.value.dir.push('asc')
	}
}

if (props.auth) {
	watch(() => $auth.initialized, () => {
		load();
	});
}

if (props.lazy || props.auth || (process.client && !isHydrating)) {
	// If the data loader requires the authenticated user state, the data will be loaded via a watcher above
	if (!props.auth || $auth.initialized) {
		onMounted(() => {
			load();
		});
	}
} else {
	await load();
}

defineExpose({
	reload,
	hasMore,
	reset
});

</script>
<style>
/*********************
  Inputs
**********************/
.table {
  position: relative;
  width: 100%;
  border-spacing: 0;
}
.table tr.table-header th {
  border-bottom: 1px solid #e6ecf5;
  color: #5e6b6b;
  opacity: 1;
  padding: 0 12px;
  user-select: none;
  font-size: 12px;
  height: 48px;
  line-height: 1.2;
  z-index: 1;
}
.table tbody tr:not(:last-child) td {
  border-bottom: 1px solid #e6ecf5;
}
.table tbody tr:not(:last-child) td.table-divider {
  border-bottom: 1px solid #d8d8d8;
}
.table tbody tr td {
  color: #465050;
  padding: 6px 12px;
  font-size: 14px;
  height: 48px;
  line-height: 1.3;
}
.table tbody tr td.table-divider {
  border-top: 1px solid #d8d8d8;
  background: #f4f4f5;
  height: 32px;
  line-height: 1.2;
}
.table tbody tr td .table-text-line {
  white-space: nowrap;
}
.table tbody tr.sub-row td {
  height: 42px;
}
.table td.sticky-right, .table th.sticky-right {
  position: sticky;
  right: 0;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  z-index: 1;
}
.table td.sticky-left, .table th.sticky-left {
  position: sticky;
  left: 0;
  z-index: 1;
}
.table.table-no-spacing tr.table-header th:first-child {
  padding-left: 0;
}
.table.table-no-spacing tr.table-header th:last-child {
  padding-right: 0;
}
.table.table-no-spacing tbody tr td:first-child {
  padding-left: 0;
}
.table.table-no-spacing tbody tr td:last-child {
  padding-right: 0;
}
@media (min-width: 640px) {
  .table.table-full-width td.sticky-right, .table.table-full-width th.sticky-right {
    right: -16px;
  }
  .table.table-full-width td.sticky-left, .table.table-full-width th.sticky-left {
    left: -16px;
  }
}
.table.table-fixed-header thead tr:last-child th {
  top: 0;
  z-index: 2;
  position: sticky;
  background: white;
}
.table.table-dense tr.table-header th {
  height: 42px;
}
.table.table-dense tbody tr td {
  height: 42px;
}
.table.table-dense tbody tr td.table-divider {
  height: 28px;
}
.table.table-dense tbody tr.sub-row td {
  height: 38px;
}
@media (min-width: 0px) and (max-width: 639px) {
  .table.table-dense tr.table-header th {
    height: 38px;
    padding: 0 6px;
  }
  .table.table-dense tbody tr td {
    height: 38px;
    padding: 4px 6px;
    font-size: 13px;
  }
  .table.table-dense tbody tr td.table-divider {
    height: 28px;
  }
  .table.table-dense tbody tr.sub-row td {
    height: 36px;
  }
  .table.table-dense.table-full-width tr.table-header th:first-child {
    padding-left: 12px;
  }
  .table.table-dense.table-full-width tr.table-header th:last-child {
    padding-right: 12px;
  }
  .table.table-dense.table-full-width tbody tr td:first-child {
    padding-left: 12px;
  }
  .table.table-dense.table-full-width tbody tr td:last-child {
    padding-right: 12px;
  }
}
.table.table-mobile-expand tbody tr td .table-text-line {
  white-space: normal;
}
</style>